// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import openPrintDialog from "@insite/client-framework/Common/Utilities/openPrintDialog";
import Zone from "@insite/client-framework/Components/Zone";
import { getCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { shallowEqual } from "react-redux";
import { css } from "styled-components";

type Props = WidgetProps;

export interface OnePageCheckoutConfirmationPageContainerStyles {
    container?: GridContainerProps;
    topGridItem?: GridItemProps;
    leftGridItem?: GridItemProps;
    spaceGridItem?: GridItemProps;
    rightGridItem?: GridItemProps;
    bottomGridItem?: GridItemProps;
    // XNG-Change
    wrapper?: InjectableCss;
    innerContainer?: InjectableCss;
    leftSection?: InjectableCss;
    headerContainer?: InjectableCss;
    addressesContainer?: InjectableCss;
    shippingContainer?: InjectableCss;
    paymentContainer?: InjectableCss;
    productsContainer?: InjectableCss;
    rightSection?: InjectableCss;
    title?: TypographyPresentationProps;
    printLink?: LinkPresentationProps;
    productsContainerTablet?: InjectableCss;
}

export const containerStyles: OnePageCheckoutConfirmationPageContainerStyles = {
    topGridItem: { width: 12 },
    leftGridItem: {
        width: [12, 12, 6, 7, 7],
        css: css`
            & > div > div:not(:last-child) {
                padding-bottom: 30px;
                border-bottom: 1px solid ${getColor("common.border")};
            }
        `,
    },
    spaceGridItem: { width: [0, 0, 1, 1, 1] },
    rightGridItem: { width: [12, 12, 5, 4, 4] },
    bottomGridItem: {
        width: 12,
        css: css`
            & > div > div:first-child {
                padding-top: 30px;
                border-top: 1px solid ${getColor("common.border")};
            }
        `,
    },
};

const styles = containerStyles;

/**
 * XNG-Changes
 * BG-2729
 * Change Structure
 * Add print button
 */
const OnePageCheckoutConfirmationPageContainer = ({ id }: Props) => {
    // XNG-Changes
    const { isLoading } = useAppSelector(state => {
        const cartState = getCartState(state, state.pages.onePageCheckoutConfirmation.cartId);
        return {
            isLoading: !cartState.value || cartState.isLoading,
        };
    }, shallowEqual);

    const printOrOpenPrintAllModal = () => {
        setTimeout(openPrintDialog, 400);
    };

    return (
        <StyledWrapper {...styles.wrapper} data-test-selectors="OnePageCheckoutConfirmationPageContainer">
            <Link
                {...styles.printLink}
                onClick={printOrOpenPrintAllModal}
                data-test-selector="orderConfirmation_PrintLink"
                disabled={isLoading}
            >
                {translate("Print")}
            </Link>
            <Typography {...styles.title}>{translate("Order Confirmation")}</Typography>
            <StyledWrapper {...styles.container}>
                <StyledWrapper {...styles.leftSection}>
                    <StyledWrapper {...styles.headerContainer}>
                        <Zone zoneName="Content0" contentId={id} />
                    </StyledWrapper>
                    <StyledWrapper {...styles.addressesContainer}>
                        <Zone zoneName="Content1" contentId={id} />
                    </StyledWrapper>
                    <StyledWrapper {...styles.shippingContainer}>
                        <Zone zoneName="Content2" contentId={id} />
                    </StyledWrapper>
                    <StyledWrapper {...styles.paymentContainer}>
                        <Zone zoneName="Content3" contentId={id} />
                    </StyledWrapper>
                    <StyledWrapper {...styles.productsContainer}>
                        <Zone zoneName="Content4" contentId={id} />
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper {...styles.rightSection}>
                    <Zone zoneName="Content5" contentId={id} normalWidth={true} />
                </StyledWrapper>
                <StyledWrapper {...styles.productsContainerTablet}>
                    <Zone zoneName="Content4" contentId={id} />
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: OnePageCheckoutConfirmationPageContainer,
    definition: {
        displayName: "Page Container",
        group: "One Page Checkout Confirmation",
        allowedContexts: ["OnePageCheckoutConfirmationPage"],
    },
};

export default widgetModule;
